/**
 * resolveExportVisibilityGate
 *
 * The single predicate every consumer that must reproduce a customer-facing view's
 * row rules runs first: the three XLSX producers — `useArTableExport`,
 * `useFormXlsxExport` and `useXlsxAttachment` — before writing a workbook, and
 * `useArTableWorkflowRowScope` before naming the rows a workflow run may touch
 * (SYD-2322). The view's `hideKitComponents` / `hideGroupComponents` flags live on
 * `parameters/organization`, so a consumer that cannot read that document would
 * silently include the kit/group member rows the grid withholds (SYD-2279). Its
 * copy therefore names neither an export nor a run.
 *
 * Only a genuine listener failure blocks: `data === null` with `error === null`
 * once loading has cleared means the server confirmed the document is absent,
 * which is the permanent state of an org whose non-orgsAdmin users cannot create
 * it — no configured view exists there, so nothing is being hidden. The internal
 * view consults no flag and is never gated.
 */

import type { OrganizationParameters } from '#types/System.Parameters.types';
import type { VisibilityTypeId } from '#types/System.types';

/** How the caller should report the refusal; `null` from the resolver means proceed. */
export type ExportVisibilityBlock = {
  severity: 'warning' | 'error';
  message: string;
};

type ResolveExportVisibilityGateParams = {
  /** The view being reproduced; `internal` and `undefined` are exempt. */
  visibilityMode: VisibilityTypeId | undefined;
  orgParams: OrganizationParameters | null;
  orgParamsLoading: boolean;
  orgParamsError: Error | null;
};

export const resolveExportVisibilityGate = ({
  visibilityMode,
  orgParams,
  orgParamsLoading,
  orgParamsError,
}: ResolveExportVisibilityGateParams): ExportVisibilityBlock | null => {
  if (!visibilityMode || visibilityMode === 'internal') return null;

  if (orgParamsLoading) {
    return {
      severity: 'warning',
      message: 'Organization details are still loading, please wait a moment and try again.',
    };
  }

  if (orgParams === null && orgParamsError !== null) {
    return {
      severity: 'error',
      message:
        "Organization details could not be loaded, so the current view's visibility rules cannot be applied. Reload the page and try again.",
    };
  }

  return null;
};
